import type { RefreshTokenFn } from "../../../shared/api/fetchWithRefresh";
import type { CalAttendee, CalEvent } from "../types";
import {
  cancelMeeting,
  createFocusBlock,
  createSeries,
  proposeNewTime,
  removeEvent,
  rescheduleEvent,
  respondToEvent,
  saveAgenda,
  setAttendees,
  type NewSeriesRequest,
} from "../services/calendarWrites";
import { TEAMS_LOCATION } from "../services/newMeeting";
import { displaySubject, isFocus } from "./day";
import { dayKeyOf, dayLabel, fd, rangeLabel } from "./time";

/*
 * A change the user made in the pane. It shows at once (`apply`) and is sent
 * (`send`) only when the Undo window ends. The copy says what will happen,
 * never "drafted": Microsoft 365 has no draft for updates or replies.
 */
export interface CalendarChange {
  readonly id: number;
  readonly kind: "reschedule" | "propose" | "respond" | "focus" | "cancel" | "attendees" | "agenda" | "create";
  /** Shown in the Undo toast. */
  readonly message: string;
  /** Shown if sending fails; the change is then undone in the pane. */
  readonly failMessage: string;
  /** The meeting to highlight on the schedule once this shows: one moved, added, or with a new time proposed. */
  readonly highlightId?: string;
  /** The change sends an invite to someone outside the organisation, so its Undo toast shows as a warning. */
  readonly warning?: boolean;
  readonly apply: (events: readonly CalEvent[]) => CalEvent[];
  readonly send: (token: string, refresh: RefreshTokenFn) => Promise<void>;
}

let nextChangeId = 1;

const firstName = (name: string) => name.trim().split(/\s+/)[0] || name;
const hasAttendees = (e: CalEvent) => e.attendees.some((a) => a.type !== "resource");
const updateSuffix = (e: CalEvent) => (hasAttendees(e) ? " · attendees will be updated" : "");
const replace = (events: readonly CalEvent[], id: string, patch: Partial<CalEvent>) =>
  events.map((e) => (e.id === id ? { ...e, ...patch } : e));

export type TimeChangeHow = "move" | "resize" | "trim" | "shorten";

/** Your own meeting, at a new time. */
export function rescheduleChange(e: CalEvent, start: Date, end: Date, how: TimeChangeHow): CalendarChange {
  const minutes = Math.round((end.getTime() - start.getTime()) / 60000);
  const subject = displaySubject(e);
  let lead: string;
  if (how === "resize") lead = `${subject} now ${fd(minutes)}`;
  else if (how === "shorten") lead = `${subject} shortened to ${fd(minutes)}`;
  else if (how === "trim") lead = `${subject} now ${rangeLabel(start, end)}`;
  else lead = e.isRecurring ? `Moved this occurrence to ${rangeLabel(start, end)}` : `Moved to ${rangeLabel(start, end)}`;
  return {
    id: nextChangeId++,
    kind: "reschedule",
    message: `${lead}${updateSuffix(e)}`,
    failMessage: `Couldn't move ${subject} — nothing was sent.`,
    highlightId: e.id,
    // Moved into another week, the meeting isn't in that week's list until it's sent, so it's added there.
    apply: (events) =>
      events.some((x) => x.id === e.id) ? replace(events, e.id, { start, end }) : [...events, { ...e, start, end }],
    send: (token, refresh) => rescheduleEvent(token, refresh, e.id, start, end),
  };
}

/** Someone else's meeting: a new time proposed to the organiser. The meeting itself stays put. */
export function proposeChange(e: CalEvent, start: Date, end: Date): CalendarChange {
  return {
    id: nextChangeId++,
    kind: "propose",
    message: `Proposing ${rangeLabel(start, end)} to ${firstName(e.organizerName)} · you'll be marked tentative`,
    failMessage: `Couldn't propose a new time for ${displaySubject(e)} — nothing was sent.`,
    highlightId: e.id,
    apply: (events) => replace(events, e.id, { response: "may" }),
    send: (token, refresh) => proposeNewTime(token, refresh, e.id, start, end),
  };
}

const RESPONSE_ACTION = {
  yes: "accept",
  may: "tentativelyAccept",
  no: "decline",
} as const;

/** How a reply is worded: while it waits to be sent ("Accepting X"), and in "Couldn't … X". */
const RESPONSE_WORDING = {
  yes: { doing: (subject: string) => `Accepting ${subject}`, failed: "accept" },
  may: { doing: (subject: string) => `Marking ${subject} tentative`, failed: "reply to" },
  no: { doing: (subject: string) => `Declining ${subject}`, failed: "decline" },
} as const;

/**
 * Reply to an invitation: Accept, Tentative or Decline. For a recurring
 * meeting this answers the one occurrence; the whole series is answered in
 * Outlook. A declined meeting shows as declined until the calendar refreshes,
 * then leaves it.
 */
export function respondChange(e: CalEvent, response: "yes" | "may" | "no"): CalendarChange {
  const subject = e.isRecurring ? "this occurrence" : displaySubject(e);
  const verb = RESPONSE_WORDING[response].doing(subject);
  const tail = e.responseRequested ? ` · ${firstName(e.organizerName)} will be notified` : " · no response is sent";
  const failVerb = RESPONSE_WORDING[response].failed;
  return {
    id: nextChangeId++,
    kind: "respond",
    message: `${verb}${tail}`,
    failMessage: `Couldn't ${failVerb} ${displaySubject(e)} — nothing was sent.`,
    apply: (events) => replace(events, e.id, { response }),
    send: (token, refresh) => respondToEvent(token, refresh, e.id, RESPONSE_ACTION[response], e.responseRequested),
  };
}

/** Decline a meeting (the clash band's option C). */
export function declineChange(e: CalEvent): CalendarChange {
  return respondChange(e, "no");
}

/** A "Focus time" block with nobody invited. */
export function focusChange(start: Date, end: Date): CalendarChange {
  const id = nextChangeId++;
  const block = localEvent({ id: `local-focus-${id}`, subject: "Focus time", start, end });
  return {
    id,
    kind: "focus",
    message: `Focus hour protected ${rangeLabel(start, end)}`,
    failMessage: "Couldn't protect the focus hour — nothing was sent.",
    highlightId: block.id,
    apply: (events) => [...events, block],
    send: (token, refresh) => createFocusBlock(token, refresh, start, end),
  };
}

/** A meeting shown in the pane before Graph has it: your own and busy, unless `e` says otherwise. */
function localEvent(e: Pick<CalEvent, "id" | "subject" | "start" | "end"> & Partial<CalEvent>): CalEvent {
  return {
    isAllDay: false,
    location: "",
    organizerName: "You",
    organizerEmail: "",
    isOrganizer: true,
    response: "organizer",
    responseRequested: false,
    showAs: "busy",
    isCancelled: false,
    attendeeCount: 0,
    attendees: [],
    joinUrl: null,
    webLink: "",
    isRecurring: false,
    seriesMasterId: null,
    hasAttachments: false,
    isDraft: false,
    allowNewTimeProposals: true,
    bodyPreview: "",
    changeKey: "",
    ...e,
  };
}

/**
 * A repeating meeting sent from the pane: its first occurrence shows at once, and the invites go out when Undo ends.
 * `externalCount` is how many of the invited are outside the organisation (0 when the warning is switched off).
 */
export function seriesChange(
  meeting: NewSeriesRequest,
  organiser: { readonly name: string; readonly email: string },
  externalCount = 0,
): CalendarChange {
  const id = nextChangeId++;
  const subject = meeting.subject || "New meeting";
  const invited = meeting.attendees.length;
  const people = invited === 1 ? "person" : "people";
  const outside = externalCount > 0 ? ` · ${externalCount} outside your organisation` : "";
  const when = `${dayLabel(dayKeyOf(meeting.start))} ${rangeLabel(meeting.start, meeting.end)} · repeats ${meeting.repeat}`;
  const first = localEvent({
    id: `local-new-${id}`,
    subject: meeting.subject,
    start: meeting.start,
    end: meeting.end,
    location: meeting.teams ? TEAMS_LOCATION : meeting.location,
    organizerName: organiser.name || "You",
    organizerEmail: organiser.email,
    responseRequested: invited > 0,
    attendeeCount: invited,
    attendees: meeting.attendees.map((a) => ({ name: a.name, email: a.email, response: "wait", type: "required" })),
    isRecurring: true,
  });
  return {
    id,
    kind: "create",
    message: invited > 0 ? `Sending the invite to ${invited} ${people}${outside} · ${when}` : `Adding ${subject} to your calendar · ${when}`,
    failMessage: invited > 0 ? `Couldn't send the invite for ${subject} — nothing was sent.` : `Couldn't add ${subject}.`,
    highlightId: first.id,
    ...(externalCount > 0 && { warning: true }),
    apply: (events) => [...events, first],
    send: (token, refresh) => createSeries(token, refresh, meeting),
  };
}

/**
 * Take your own meeting off the calendar. With people invited it is cancelled
 * and they are told; an appointment with nobody invited is simply removed.
 */
export function cancelChange(e: CalEvent): CalendarChange {
  const subject = displaySubject(e);
  const invited = hasAttendees(e);
  let message: string;
  if (isFocus(e)) message = "Focus time removed";
  else if (invited) message = `Cancelling ${e.isRecurring ? "this occurrence" : subject} · attendees will get a cancellation`;
  else message = `${e.isRecurring ? "This occurrence" : subject} removed from your calendar`;
  return {
    id: nextChangeId++,
    kind: "cancel",
    message,
    failMessage: `Couldn't ${invited ? "cancel" : "remove"} ${subject} — nothing was sent.`,
    apply: (events) => events.filter((x) => x.id !== e.id),
    send: (token, refresh) => (invited ? cancelMeeting(token, refresh, e.id) : removeEvent(token, refresh, e.id)),
  };
}

const withAttendees = (attendees: readonly CalAttendee[]): Partial<CalEvent> => ({
  attendees,
  attendeeCount: attendees.filter((a) => a.type !== "resource").length,
});

const AVAILABILITY_NOTE = { free: " — free at this time", busy: " — busy at this time", unknown: "" } as const;

/**
 * Your own meeting: invite someone else. `availability` is what their calendar showed, when it was readable.
 * `external` is true when they are outside the organisation and the warning is switched on.
 */
export function addAttendeeChange(
  e: CalEvent,
  person: { readonly name: string; readonly email: string },
  availability: "free" | "busy" | "unknown",
  external = false,
): CalendarChange {
  const attendees: CalAttendee[] = [...e.attendees, { name: person.name, email: person.email, response: "wait", type: "required" }];
  const who = firstName(person.name);
  const note = AVAILABILITY_NOTE[availability];
  return {
    id: nextChangeId++,
    kind: "attendees",
    message: `${who} added${note}${external ? " · outside your organisation" : ""} · they'll get the invite`,
    ...(external && { warning: true }),
    failMessage: `Couldn't add ${who} to ${displaySubject(e)} — nothing was sent.`,
    apply: (events) => replace(events, e.id, withAttendees(attendees)),
    send: (token, refresh) => setAttendees(token, refresh, e.id, attendees),
  };
}

/** Your own meeting: take someone off the invite. */
export function removeAttendeeChange(e: CalEvent, email: string): CalendarChange {
  const person = e.attendees.find((a) => a.email === email);
  const attendees = e.attendees.filter((a) => a.email !== email);
  const who = firstName(person?.name ?? email);
  return {
    id: nextChangeId++,
    kind: "attendees",
    message: `${who} removed · they'll get a cancellation`,
    failMessage: `Couldn't remove ${who} from ${displaySubject(e)} — nothing was sent.`,
    apply: (events) => replace(events, e.id, withAttendees(attendees)),
    send: (token, refresh) => setAttendees(token, refresh, e.id, attendees),
  };
}

/** Your own meeting: save the agenda block in the invite. An empty agenda removes the block. */
export function agendaChange(e: CalEvent, agenda: string): CalendarChange {
  const text = agenda.trim();
  return {
    id: nextChangeId++,
    kind: "agenda",
    message: `${text ? "Agenda saved to the invite" : "Agenda removed from the invite"}${updateSuffix(e)}`,
    failMessage: `Couldn't save the agenda for ${displaySubject(e)} — nothing was sent.`,
    apply: (events) => replace(events, e.id, { pendingAgenda: text }),
    send: (token, refresh) => saveAgenda(token, refresh, e.id, text),
  };
}

/** What dragging a meeting does: you move your own; you can only propose a new time for someone else's. */
export function moveModeFor(e: CalEvent): "reschedule" | "propose" | "none" {
  if (e.isAllDay || e.isCancelled || e.response === "no" || e.id.startsWith("local-")) return "none";
  if (e.isOrganizer) return "reschedule";
  return e.allowNewTimeProposals ? "propose" : "none";
}
